import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin, Megaphone, Phone, Mail, Play } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useFetch, fileUrl, fmtDate, usePageTitle } from "@/lib/api";
import { SITE, COMMUNITIES, DIVISIONS, IMG, TRYOUT, BRAND } from "@/lib/content";
import { Reveal, SectionHeading, PalmSilhouette, EmptyState } from "@/components/site/Bits";
import { Countdown } from "@/components/site/Countdown";
import { AthleteCard } from "@/components/site/AthleteCard";

const Hero = () => {
  const { settings } = useSite();
  const headline = settings.hero_headline || "THE COAST'S BEST. ONE TEAM.";
  const [a, b] = headline.includes(". ") ? [headline.split(". ")[0] + ".", headline.split(". ").slice(1).join(". ")] : [headline, ""];
  return (
    <section className="relative min-h-[100svh] flex items-end overflow-hidden grain bg-pitch" data-testid="home-hero">
      <img src={settings.hero_image_url ? fileUrl(settings.hero_image_url) : IMG.hero} alt="Youth football players at the line of scrimmage under the lights" className="absolute inset-0 h-full w-full object-cover animate-slowzoom" />
      <div className="absolute inset-0 bg-gradient-to-r from-pitch via-navy/75 to-navy/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-pitch via-pitch/20 to-transparent" />
      <PalmSilhouette className="absolute right-[-40px] bottom-0 h-[62%] text-pitch/70 hidden sm:block" />
      <motion.img src={settings.logo_url ? fileUrl(settings.logo_url) : BRAND.logo} alt="Team Tri County All-Star Elite Classic logo"
        initial={{ opacity: 0, scale: 0.85, rotate: -4 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="absolute right-[6%] top-[22%] hidden lg:block w-[30vw] max-w-[440px] drop-shadow-[0_20px_60px_rgba(0,0,0,0.6)]" data-testid="hero-logo" />
      <div className="relative wrap pt-40 pb-14 sm:pb-20">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="eyebrow mb-6 flex items-center gap-3">
          <span className="h-px w-12 bg-gold" /> Treasure Coast, Florida • 2027 Allstar Season
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="h-display text-6xl sm:text-7xl lg:text-[8.5rem] max-w-5xl" data-testid="hero-headline">
          {a}<br /><span className="text-gold">{b}</span>
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.6 }}
          className="mt-6 font-display text-xl sm:text-2xl font-bold uppercase italic tracking-wide text-slate-200" data-testid="hero-subheading">
          “{settings.hero_subheading || SITE.slogan}”
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6 }} className="mt-10 flex flex-col sm:flex-row gap-4">
          <Link to="/tryouts" className="btn-gold !text-lg !px-9 !py-4" data-testid="hero-register-button">Register for Tryouts <ArrowRight className="h-5 w-5" /></Link>
          <Link to="/about" className="btn-line !text-lg !px-9 !py-4" data-testid="hero-explore-button">Explore Tri County</Link>
        </motion.div>
        <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 border-t border-white/15">
          {[["8", "Age Divisions"], ["6–13", "Eligible Ages"], ["1", "Coast. One Team."], ["AUG", "National Allstars Classic"]].map(([n, l]) => (
            <div key={l} className="pt-5 pr-4">
              <p className="font-display text-4xl sm:text-5xl font-black italic text-gold leading-none">{n}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{l}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CountdownBand = ({ events }) => {
  const { settings } = useSite();
  const upcoming = (events || []).filter((e) => e.date && e.date >= new Date().toISOString().slice(0, 10)).slice(0, 3);
  return (
    <section className="relative bg-navy border-y border-line" data-testid="home-countdown-section">
      <div className="wrap py-12 grid gap-10 lg:grid-cols-[1.2fr_1fr] items-center">
        <div>
          <p className="eyebrow mb-3">Next Up • {TRYOUT.date}</p>
          <h2 className="h-display text-3xl sm:text-4xl mb-6">2027 Allstar Tryouts — Countdown</h2>
          <Countdown target={settings.tryout_datetime || "2026-10-25T10:00:00-04:00"} testid="home-countdown" />
        </div>
        <div className="space-y-2" data-testid="home-upcoming-events">
          {upcoming.map((e) => (
            <Link key={e.id} to={`/events/${e.slug || e.id}`} className="group flex items-center gap-5 bg-pitch/50 border border-line p-4 hover:border-gold transition-colors" data-testid={`home-event-${e.slug || e.id}`}>
              <div className="w-16 text-center">
                <p className="font-mono text-[10px] uppercase text-gold">{fmtDate(e.date, { month: "short" })}</p>
                <p className="font-display text-3xl font-black leading-none">{fmtDate(e.date, { day: "numeric" })}</p>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg font-extrabold uppercase italic truncate">{e.title}</p>
                <p className="text-xs text-slate-400 truncate">{e.location}</p>
              </div>
              <ArrowUpRight className="h-5 w-5 text-slate-500 group-hover:text-gold transition-colors" />
            </Link>
          ))}
          <Link to="/events" className="inline-flex pt-2 text-sm font-semibold uppercase tracking-wider text-gold hover:text-white" data-testid="home-all-events-link">Full event calendar →</Link>
        </div>
      </div>
    </section>
  );
};

const RoadToAug = () => {
  const steps = [
    ["01", "Tryouts", "Oct 25, 2026", "Fully padded selection at PSLAA Field. Every athlete earns their spot."],
    ["02", "Practices", "After regular season", "Selected teams begin practice once their regular season concludes."],
    ["03", "AUG Florida Showcase", "Jan 9, 2027", "Tri County hosts Florida's regional All-Star organizations at PSLAA."],
    ["04", "AUG National Allstars Classic", "Date TBA", "The national stage. Representing the Treasure Coast together."],
  ];
  return (
    <section className="relative overflow-hidden bg-pitch py-24 sm:py-32" data-testid="road-to-aug-section">
      <img src={IMG.lights} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-pitch via-pitch/80 to-pitch" />
      <div className="relative wrap">
        <Reveal>
          <p className="eyebrow mb-4">The Postseason Path</p>
          <h2 className="h-display text-5xl sm:text-7xl lg:text-8xl max-w-5xl">The Road to <span className="text-gold">AUG</span> Starts Here.</h2>
        </Reveal>
        <div className="mt-16 grid gap-px bg-line/60 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map(([n, t, d, b], i) => (
            <Reveal key={n} delay={i * 0.1} className="bg-pitch p-7 group hover:bg-navy transition-colors">
              <p className="font-display text-6xl font-black italic text-stroke">{n}</p>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{d}</p>
              <p className="mt-2 font-display text-2xl font-black uppercase italic">{t}</p>
              <p className="mt-3 text-sm text-slate-400">{b}</p>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/tryouts" className="btn-gold" data-testid="road-register-button">Register for Tryouts</Link>
          <Link to="/events" className="btn-line" data-testid="road-events-button">View Events</Link>
        </div>
      </div>
    </section>
  );
};

const NewsDesk = () => {
  const { data: ann } = useFetch("/content/announcements");
  const { data: articles } = useFetch("/content/articles");
  const top = (articles || [])[0];
  return (
    <section className="bg-navy py-20 sm:py-28" data-testid="home-news-section">
      <div className="wrap grid gap-12 lg:grid-cols-[1.5fr_1fr]">
        <div>
          <SectionHeading eyebrow="Media Center" title="Latest From the Coast" />
          {top ? (
            <Link to={`/media/${top.id}`} className="group mt-10 block relative overflow-hidden bg-pitch aspect-[16/10]" data-testid="home-top-story">
              <img src={top.cover_url ? fileUrl(top.cover_url) : IMG.night} alt="" className="absolute inset-0 h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-pitch via-pitch/40 to-transparent" />
              <div className="absolute bottom-0 p-6 sm:p-8">
                <span className="bg-[#CC0000] px-2 py-1 font-mono text-[10px] uppercase tracking-widest">{top.category}</span>
                <h3 className="mt-3 font-display text-3xl sm:text-4xl font-black uppercase italic leading-none max-w-xl group-hover:text-gold transition-colors">{top.title}</h3>
                <p className="mt-3 text-sm text-slate-300 max-w-lg">{top.excerpt}</p>
              </div>
            </Link>
          ) : <EmptyState title="Stories coming soon" testid="home-news-empty" />}
        </div>
        <div>
          <p className="eyebrow mb-6 flex items-center gap-2"><Megaphone className="h-4 w-4" /> Announcements</p>
          <div className="space-y-px bg-line/60" data-testid="home-announcements">
            {(ann || []).slice(0, 5).map((a) => (
              <div key={a.id} className="bg-navy py-5 pr-2" data-testid={`announcement-${a.id}`}>
                <div className="flex items-center gap-3">
                  {a.pinned && <span className="h-2 w-2 bg-gold" />}
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{a.category} • {fmtDate(a.date)}</p>
                </div>
                <p className="mt-1 font-display text-xl font-extrabold uppercase italic">{a.title}</p>
                <p className="text-sm text-slate-300">{a.body}</p>
              </div>
            ))}
            {ann && ann.length === 0 && <p className="bg-navy py-5 text-sm text-slate-400">No announcements right now.</p>}
          </div>
          <Link to="/media" className="mt-6 inline-flex text-sm font-semibold uppercase tracking-wider text-gold hover:text-white" data-testid="home-media-link">Visit the Media Center →</Link>
        </div>
      </div>
    </section>
  );
};

const NextUp = () => {
  const { data } = useFetch("/content/athletes");
  const featured = (data || []).filter((a) => a.featured).slice(0, 4);
  const list = featured.length ? featured : (data || []).slice(0, 4);
  return (
    <section className="relative bg-pitch py-20 sm:py-28 overflow-hidden" data-testid="home-next-up-section">
      <p className="absolute -left-4 top-6 h-display text-[9rem] sm:text-[14rem] text-stroke-white select-none pointer-events-none whitespace-nowrap">NEXT UP</p>
      <div className="relative wrap">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <SectionHeading eyebrow="Athlete Spotlight" title={<>The Coast's <span className="text-gold">Next Up.</span></>} sub="Recognizing the standout athletes representing Florida's Treasure Coast." />
          <Link to="/athletes" className="btn-line shrink-0" data-testid="home-athletes-link">All Athletes</Link>
        </div>
        <div className="mt-12">
          {list.length ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{list.map((a, i) => <AthleteCard key={a.id} athlete={a} index={i} />)}</div>
          ) : (
            <div className="grid gap-5 lg:grid-cols-[1.1fr_1fr] items-stretch">
              <Link to="/athletes" className="group relative overflow-hidden border border-gold/50 bg-navy" data-testid="home-invitation-feature">
                <img src={BRAND.poster} alt="Official Tri County Allstar invitation graphic" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                <span className="absolute left-4 top-4 bg-gold px-3 py-1 font-display font-extrabold uppercase text-navy">Official Allstar Invitation</span>
              </Link>
              <div className="grid grid-rows-[1fr_auto] gap-5">
                <div className="relative overflow-hidden bg-navy min-h-[280px]">
                  <img src={BRAND.jersey} alt="Tri County Allstars athlete wearing jersey number 10" className="absolute inset-0 h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-pitch via-transparent to-transparent" />
                  <p className="absolute bottom-5 left-5 h-display text-4xl">Wear the <span className="text-gold">Coast.</span></p>
                </div>
                <EmptyState testid="home-athletes-empty" title="Recruiting cards drop soon." body="Athlete profiles are published only with parent or guardian permission. Check back after tryouts for the first class of The Coast's Next Up." />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const Divisions = () => {
  const { data } = useFetch("/content/teams");
  const teams = data || DIVISIONS.map((d) => ({ ...d, id: d.slug }));
  return (
    <section className="bg-navy py-20 sm:py-28" data-testid="home-divisions-section">
      <div className="wrap">
        <SectionHeading eyebrow="2027 Teams" title="Eight Divisions. One Standard." sub="From 6U to 8th Grade, every Tri County Allstars team is led by a dedicated head coach." />
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-px bg-line/60">
          {teams.map((t, i) => (
            <Link key={t.slug} to={`/teams/${t.slug}`} className="group relative bg-navy p-6 sm:p-8 overflow-hidden hover:bg-gold transition-colors duration-300" data-testid={`home-division-${t.slug}`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 group-hover:text-navy/70">Division {String(i + 1).padStart(2, "0")}</p>
              <p className="mt-2 font-display text-5xl sm:text-6xl font-black italic group-hover:text-navy">{t.name}</p>
              {t.head_coach && <p className="mt-3 text-xs uppercase tracking-wider text-slate-300 group-hover:text-navy">HC • {t.head_coach}</p>}
              <ArrowUpRight className="absolute right-5 top-5 h-5 w-5 text-slate-500 group-hover:text-navy transition-transform group-hover:rotate-45" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const Mission = () => {
  const { settings } = useSite();
  return (
    <section className="relative overflow-hidden bg-pitch py-20 sm:py-28" data-testid="home-mission-section">
      <PalmSilhouette className="absolute left-[-60px] bottom-0 h-[90%] text-navy/60" />
      <div className="relative wrap grid gap-12 lg:grid-cols-2 items-center">
        <Reveal>
          <p className="eyebrow mb-4">Our Mission</p>
          <h2 className="h-display text-5xl sm:text-6xl">Different Parks.<br />One Coast.<br /><span className="text-gold">One Mission.</span></h2>
          <div className="mt-10 grid grid-cols-3 gap-2" data-testid="mission-photos">
            {[BRAND.jersey, BRAND.ring, BRAND.gloves].map((src, i) => (
              <div key={src} className={`overflow-hidden bg-navy ${i === 1 ? "translate-y-6" : ""}`}>
                <img src={src} alt="Tri County Allstars athlete" loading="lazy" className="aspect-[3/4] w-full object-cover transition-transform duration-700 hover:scale-105" />
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-base sm:text-lg text-slate-300 leading-relaxed">{settings.mission_text}</p>
          <p className="mt-5 text-sm text-slate-400 leading-relaxed">We unite talented athletes who compete against each other during the regular season and give them the opportunity to represent their communities together on a bigger stage.</p>
          <div className="mt-8 flex flex-wrap gap-2">
            {COMMUNITIES.map((c) => <span key={c} className="border border-line px-3 py-1.5 text-xs uppercase tracking-wider text-slate-200 flex items-center gap-1.5"><MapPin className="h-3 w-3 text-gold" />{c}</span>)}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Highlights = () => {
  const { data: events } = useFetch("/content/events");
  const { data: articles } = useFetch("/content/articles");
  const videos = (articles || []).filter((a) => a.video_url).slice(0, 3);
  const withResults = (events || []).filter((e) => e.results || (e.scores || []).length);
  return (
    <section className="bg-navy py-20 sm:py-28" data-testid="home-highlights-section">
      <div className="wrap grid gap-14 lg:grid-cols-2">
        <div>
          <SectionHeading eyebrow="Tournament Highlights" title="On the Big Stage" />
          <div className="mt-8 space-y-3">
            {withResults.length ? withResults.map((e) => (
              <Link key={e.id} to={`/events/${e.slug || e.id}`} className="block card-navy p-5 hover:border-gold" data-testid={`home-result-${e.id}`}>
                <p className="font-display text-xl font-extrabold uppercase italic">{e.title}</p>
                <p className="text-sm text-slate-300 mt-1 line-clamp-2">{e.results || `${e.scores.length} score(s) posted`}</p>
              </Link>
            )) : <EmptyState testid="home-results-empty" title="Results posted after kickoff" body="Official scores and tournament results will appear here once games are played." />}
          </div>
        </div>
        <div>
          <SectionHeading eyebrow="Media Highlights" title="Watch the Coast" />
          <div className="mt-8 space-y-3">
            {videos.length ? videos.map((v) => (
              <Link key={v.id} to={`/media/${v.id}`} className="group flex items-center gap-4 card-navy p-4 hover:border-gold" data-testid={`home-video-${v.id}`}>
                <span className="flex h-12 w-12 shrink-0 items-center justify-center bg-gold text-navy"><Play className="h-5 w-5 fill-current" /></span>
                <p className="font-display text-lg font-extrabold uppercase italic group-hover:text-gold">{v.title}</p>
              </Link>
            )) : <EmptyState testid="home-videos-empty" title="Highlights loading up" body="Game highlights, interviews and All Things Sports features will be published in the Media Center." />}
          </div>
        </div>
      </div>
    </section>
  );
};

const SponsorWall = () => {
  const { data } = useFetch("/content/sponsors");
  return (
    <section className="bg-pitch py-20 border-y border-line" data-testid="home-sponsors-section">
      <div className="wrap">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <SectionHeading eyebrow="Sponsor Recognition" title="Partners of the Coast" />
          <Link to="/sponsors" className="btn-gold shrink-0" data-testid="home-partner-button">Partner With Us</Link>
        </div>
        <div className="mt-10">
          {data && data.length ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-line/60">
              {data.map((s) => (
                <a key={s.id} href={s.website || undefined} target="_blank" rel="noreferrer" className="flex h-32 items-center justify-center bg-pitch p-6 hover:bg-navy transition-colors" data-testid={`home-sponsor-${s.id}`}>
                  {s.logo_url ? <img src={fileUrl(s.logo_url)} alt={s.name} className="max-h-16 max-w-full object-contain" /> : <span className="font-display text-xl font-black uppercase italic">{s.name}</span>}
                </a>
              ))}
            </div>
          ) : (
            <EmptyState testid="home-sponsors-empty" title="Become a founding partner." body="Local businesses and community organizations can support Tri County Allstars athletes this season." />
          )}
        </div>
      </div>
    </section>
  );
};

const ContactStrip = () => (
  <section className="relative bg-gold text-navy overflow-hidden" data-testid="home-contact-section">
    <div className="wrap py-14 grid gap-8 lg:grid-cols-[1.2fr_1fr] items-center">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.3em]">Questions? Talk to Tri County</p>
        <h2 className="h-display text-4xl sm:text-6xl mt-3">Coach KP • President / Program Director</h2>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
        <a href={SITE.tel} className="inline-flex items-center justify-center gap-2 bg-navy text-white px-6 py-4 font-display text-lg font-extrabold uppercase hover:bg-pitch transition-colors" data-testid="home-call-button"><Phone className="h-5 w-5" /> {SITE.phone}</a>
        <a href={SITE.mailto} className="inline-flex items-center justify-center gap-2 border-2 border-navy px-6 py-4 font-display text-lg font-extrabold uppercase hover:bg-navy hover:text-white transition-colors" data-testid="home-email-button"><Mail className="h-5 w-5" /> Email Us</a>
      </div>
    </div>
  </section>
);

export default function Home() {
  usePageTitle("");
  const { data: events } = useFetch("/content/events");
  return (
    <div data-testid="home-page">
      <Hero />
      <CountdownBand events={events} />
      <RoadToAug />
      <NewsDesk />
      <NextUp />
      <Divisions />
      <Mission />
      <Highlights />
      <SponsorWall />
      <ContactStrip />
    </div>
  );
}
